import { browser } from '$app/environment';
import { localStorageSupported } from '$lib/helper/browser';
import { detectLocale, type AppLocale } from '$lib/helper/locale';
import { DateTime } from 'luxon';
import { derived, get, writable } from 'svelte/store';

export const selectedLocale = writable<AppLocale>(detectLocale());

type Translations = {
	[key in AppLocale]?: {
		[key: string]: string;
	};
};

// Load the json file that contains all the translations.
// format: { "homepage.title": "Hello, World!" }
const translations = writable<Translations>({});

selectedLocale.subscribe((locale) => {
	if (!locale) return () => {};

	if (browser && localStorageSupported()) {
		localStorage.setItem('locale', locale);
	}

	// Check if the hostname is widget or app
	let isWidget = false;
	if (browser) {
		const path = window.location.pathname;
		isWidget = path.startsWith('/widget');
	}

	import(`$lib/locale/${isWidget ? 'widget.' : ''}${locale}.js`)
		.then((module) => {
			// add translation locales to the store
			translations.update((t) => {
				t[locale] = module.default;
				return t;
			});
		})
		.catch((error) => {
			console.error(`Error loading translations for ${locale}:`, error);
		});

	if (!browser) {
		// Load the widget translations if we are on server side (load both app and widget translations)
		import(`$lib/locale/widget.${locale}.js`)
			.then((module) => {
				// add translation locales to the store
				get(translations)[locale] = {
					...get(translations)[locale],
					...module.default
				};
			})
			.catch((error) => {
				console.error(`Error loading widget translations for ${locale}:`, error);
			});
	}

	return () => {};
});

// Translation function
function translate(translations: Translations, key: string, vars: TranslationVars = {}) {
	// Check if translations are loaded, if not, return an empty string.
	const locale = get(selectedLocale);
	if (!translations || !translations[locale]) return '';

	// if key doesn't exist, return the key
	let text = key;
	if (translations[locale][key] === undefined) {
		const msg = `Translation for "${key}" in "${locale}" locale not found`;
		console.error(msg);
		if (typeof window !== 'undefined') window.Sentry?.captureMessage(msg);
	} else {
		text = translations[locale][key];
	}

	// Replace any passed-in variables in the translation string.
	Object.keys(vars).forEach((k) => {
		const regex = new RegExp(`{{${k}}}`, 'g');
		text = text.replace(regex, vars[k]);
	});

	return text.replace(/\n/g, '<br />');
}

// Get translations from the current local which match the given matcher function.
export const findTranslations = derived(
	translations,
	($translations) => (matcher: (key: string, value: string) => boolean) => {
		const locale = get(selectedLocale);
		if (!$translations || !$translations[locale]) return [];

		const results = [];
		const localTranslations = $translations[locale];
		for (const key in localTranslations) {
			if (
				Object.prototype.hasOwnProperty.call(localTranslations, key) &&
				matcher(key, localTranslations[key])
			) {
				results.push({ key, value: localTranslations[key] });
			}
		}

		return results;
	}
);

export type TranslationVars = {
	[key: string]: string;
};

export enum CurrencyCode {
	EUR = 'EUR'
}

export const t = derived(
	translations,
	($translations) =>
		(key: string, vars: TranslationVars = {}) =>
			translate($translations, key, vars)
);

export function formatCurrency(
	value: number,
	currencyCode: CurrencyCode,
	showCents = true,
	centsPrecision = 2
) {
	return new Intl.NumberFormat(get(selectedLocale), {
		style: 'currency',
		currency: currencyCode,
		minimumFractionDigits: showCents ? centsPrecision : 0,
		maximumFractionDigits: showCents ? centsPrecision : 0
	}).format(value);
}

export function formatNumber(value: number) {
	return new Intl.NumberFormat(get(selectedLocale)).format(value);
}

type FormatDateOptions =
	| 'ISO'
	| 'withYear'
	| 'withSlash'
	| 'monthText'
	| Intl.DateTimeFormatOptions
	| null;

export const businessTimezone = writable<string>('UTC'); // Pacific/Kiritimati (UTC+14), Pacific/Howland (UTC-12)

export function formatDate(
	date: DateTime | undefined,
	options: FormatDateOptions = null,
	params: {
		addYearIfDifferent?: boolean;
		useShortNames?: boolean;
		timezone?: string;
	} = {}
) {
	if (!date) return '';

	const timezone = params.timezone ?? get(businessTimezone);
	const addYearIfDifferent = params.addYearIfDifferent ?? true;
	const useShortNames = params.useShortNames ?? true;

	if (timezone.toLowerCase() === 'utc') {
		console.error('Timezone is not set for formatDate');
	}

	// Today of the businessTimezone
	const now = DateTime.now().setZone(timezone);
	const targetDate = date.setZone(timezone);

	let dateOptions: Intl.DateTimeFormatOptions = {};
	if (!options) {
		dateOptions = { weekday: 'short', day: 'numeric', month: 'short' };
	} else if (options === 'ISO') {
		return date.toISODate() ?? 'null';
	} else if (options === 'withYear') {
		dateOptions = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
	} else if (options === 'monthText') {
		dateOptions = { day: 'numeric', month: 'short', year: 'numeric' };
	} else if (options === 'withSlash') {
		dateOptions = { day: 'numeric', month: 'numeric', year: 'numeric' };
	} else {
		dateOptions = options;
	}

	if (targetDate.year !== now.year && addYearIfDifferent) {
		dateOptions.year = 'numeric';
	}

	if (useShortNames) {
		if (now.toISODate() === targetDate.toISODate()) {
			return get(t)('Today');
		} else if (now.toISODate() === targetDate.plus({ days: 1 }).toISODate()) {
			return get(t)('Yesterday');
		} else if (now.toISODate() === targetDate.minus({ days: 1 }).toISODate()) {
			return get(t)('Tomorrow');
		}
	}

	return targetDate.toLocaleString(dateOptions, { locale: get(selectedLocale) });
}

type FormatDateTimeOptions = {
	timeStyle?: 'short' | 'medium' | 'long' | 'full';
};

export function formatTime(
	date: DateTime,
	options: FormatDateTimeOptions = {},
	params: { timezone?: string } = {}
) {
	const timezone = params.timezone ?? get(businessTimezone);

	if (timezone.toLowerCase() === 'utc') {
		console.error('Timezone is not set for formatTime');
	}

	return date
		.setZone(timezone)
		.toLocaleString(
			{ timeStyle: options.timeStyle ?? 'short' },
			{ locale: get(selectedLocale) }
		);
}
